import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { madeInsAPI } from '../../services/garageApi';
import {
  Save,
  X,
  MapPin,
  AlertCircle,
  ArrowLeft,
  Loader2,
  Globe,
  Info
} from 'lucide-react';

// Custom hook for form validation with enhanced rules
const useFormValidation = () => {
  const [errors, setErrors] = useState({});

  const validateField = useCallback((name, value) => {
    const trimmedValue = value?.toString().trim() || '';
    const fieldErrors = {};

    switch (name) {
      case 'name':
        if (!trimmedValue) {
          fieldErrors.name = 'Location name is required';
        } else if (trimmedValue.length < 2) {
          fieldErrors.name = 'Location name must be at least 2 characters';
        } else if (trimmedValue.length > 100) {
          fieldErrors.name = 'Location name must not exceed 100 characters';
        } else if (!/^[a-zA-Z\s\-.,()]+$/.test(trimmedValue)) {
          fieldErrors.name = 'Location name can only contain letters, spaces, and basic punctuation';
        }
        break;
      default:
        break;
    }

    return fieldErrors;
  }, []);

  const validateForm = useCallback((formData) => {
    const allErrors = {};

    Object.keys(formData).forEach(field => {
      const fieldErrors = validateField(field, formData[field]);
      Object.assign(allErrors, fieldErrors);
    });

    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  }, [validateField]);

  const clearFieldError = useCallback((fieldName) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const setFieldError = useCallback((fieldName, error) => {
    setErrors(prev => ({ ...prev, [fieldName]: error }));
  }, []);

  return {
    errors,
    validateField,
    validateForm,
    clearFieldError,
    setFieldError,
    setErrors
  };
};

// Custom hook for managing MadeIn data
const useMadeInData = (id) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMadeIn = useCallback(async () => {
    if (!id) {
      setError('Invalid location ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await madeInsAPI.show(id);
      const madeInData = response?.data?.data || response?.data;

      if (!madeInData) {
        throw new Error('Made in location not found');
      }

      setData(madeInData);
    } catch (error) {
      console.error('Error fetching made in location:', error);
      const errorMessage = error.response?.data?.message ||
          error.message ||
          'Failed to load made in location';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  return { data, loading, error, fetchMadeIn, refetch: fetchMadeIn };
};

// Enhanced form data hook with change tracking
const useFormData = (initialData) => {
  const [formData, setFormData] = useState({ name: '' });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState({ name: '' });

  // Initialize form when data is available
  useEffect(() => {
    if (initialData) {
      const initial = {
        name: initialData.name || ''
      };
      setFormData(initial);
      setOriginalData(initial);
      setHasUnsavedChanges(false);
    }
  }, [initialData]);

  const updateFormData = useCallback((name, value) => {
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Check if data has changed
      const hasChanged = JSON.stringify(newData) !== JSON.stringify(originalData);
      setHasUnsavedChanges(hasChanged);
      return newData;
    });
  }, [originalData]);

  const resetForm = useCallback(() => {
    setFormData(originalData);
    setHasUnsavedChanges(false);
  }, [originalData]);

  return {
    formData,
    hasUnsavedChanges,
    updateFormData,
    resetForm,
    setHasUnsavedChanges
  };
};

const EditMadeIn = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Refs
  const nameInputRef = useRef(null);
  const formRef = useRef(null);

  // Custom hooks
  const { data, loading, error, fetchMadeIn } = useMadeInData(id);
  const { formData, hasUnsavedChanges, updateFormData, resetForm, setHasUnsavedChanges } = useFormData(data);
  const { errors, validateField, validateForm, clearFieldError, setErrors } = useFormValidation();

  // Focus name input when loaded
  useEffect(() => {
    if (!loading && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [loading]);

  // Fetch data on mount
  useEffect(() => {
    fetchMadeIn();
  }, [fetchMadeIn]);

  // Warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    updateFormData(name, value);

    // Clear previous error for this field
    if (errors[name]) {
      clearFieldError(name);
    }

    // Real-time validation for better UX (debounced)
    if (submitAttempted) {
      setTimeout(() => {
        const fieldErrors = validateField(name, value);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...fieldErrors }));
        }
      }, 300);
    }
  }, [updateFormData, errors, clearFieldError, submitAttempted, validateField, setErrors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if (!validateForm(formData)) {
      // Focus first field with error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField && formRef.current) {
        const errorElement = formRef.current.querySelector(`[name="${firstErrorField}"]`);
        errorElement?.focus();
      }
      return;
    }

    try {
      setSaving(true);
      setErrors({});

      const payload = {
        name: formData.name.trim()
      };

      await madeInsAPI.update(id, payload);
      setHasUnsavedChanges(false);

      navigate('/made-ins', {
        state: {
          message: 'Made in location updated successfully!',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error updating made in location:', error);

      // Handle different error types
      if (error.response?.status === 422 && error.response?.data?.errors) {
        // Validation errors from server
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else if (error.response?.status === 404) {
        setErrors({ submit: 'Made in location not found. It may have been deleted.' });
      } else if (error.response?.status >= 500) {
        setErrors({ submit: 'Server error. Please try again later.' });
      } else {
        setErrors({ submit: 'Failed to update made in location. Please try again.' });
      }

      // Scroll to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  }, [formData, validateForm, errors, id, navigate, setErrors, setHasUnsavedChanges]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/made-ins');
      }
    } else {
      navigate('/made-ins');
    }
  }, [hasUnsavedChanges, navigate]);

  const handleRetry = useCallback(() => {
    fetchMadeIn();
  }, [fetchMadeIn]);

  const handleReset = useCallback(() => {
    if (window.confirm('Are you sure you want to reset all changes?')) {
      resetForm();
      setErrors({});
      setSubmitAttempted(false);
    }
  }, [resetForm, setErrors]);

  // Memoized form validation state
  const isFormValid = useMemo(() => {
    return Object.keys(errors).length === 0 && formData.name.trim().length > 0;
  }, [errors, formData.name]);

  // Loading state
  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
            <p className="mt-2 text-gray-600">Loading made in location...</p>
          </div>
        </div>
    );
  }

  // Error state
  if (error) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Unable to Load Location</h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <div className="mt-6 space-x-3">
              <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Try Again
              </button>
              <Link
                  to="/made-ins"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to Locations
              </Link>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <button
                  onClick={handleCancel}
                  className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-1" />
                Back to Made In Locations
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Globe className="h-6 w-6 mr-2 text-blue-600" />
                  Edit Made In Location
                </h1>
                <p className="text-gray-600 mt-1">Update manufacturing origin details</p>
              </div>

              {hasUnsavedChanges && (
                  <div className="flex items-center space-x-2">
                    <button
                        onClick={handleReset}
                        className="text-sm text-gray-600 hover:text-gray-800 underline"
                    >
                      Reset Changes
                    </button>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                  Unsaved changes
                </span>
                  </div>
              )}
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
              <div className="mb-6 rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error updating location</h3>
                    <p className="mt-1 text-sm text-red-700">{errors.submit}</p>
                  </div>
                </div>
              </div>
          )}

          {/* Form */}
          <div className="bg-white shadow-sm rounded-lg">
            <form ref={formRef} onSubmit={handleSubmit} noValidate className="p-6 space-y-6">
              {/* Info Banner */}
              <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
                <div className="flex">
                  <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      This represents the country or region where tires are manufactured.
                      Use standard country names for consistency.
                    </p>
                  </div>
                </div>
              </div>

              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Location Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                      ref={nameInputRef}
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Japan, Thailand, Germany"
                      className={`block w-full pl-10 pr-3 py-3 border rounded-md shadow-sm focus:outline-none focus:ring-2 transition-colors ${
                          errors.name
                              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      aria-invalid={errors.name ? 'true' : 'false'}
                      aria-describedby={errors.name ? 'name-error' : 'name-help'}
                  />
                </div>

                {errors.name && (
                    <p id="name-error" className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.name}
                    </p>
                )}

                <p id="name-help" className="mt-2 text-sm text-gray-500">
                  Enter the country or region where tires are manufactured
                </p>
              </div>

              {/* Form Actions */}
              <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  * Required fields
                </div>

                <div className="flex space-x-3">
                  <button
                      type="button"
                      onClick={handleCancel}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>

                  <button
                      type="submit"
                      disabled={saving || !isFormValid}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Updating...
                        </>
                    ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Update Location
                        </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Keyboard Shortcuts Info */}
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <p className="text-xs text-gray-600">
              <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Ctrl+S</kbd> to save •
              <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs ml-1">Esc</kbd> to cancel
            </p>
          </div>
        </div>
      </div>
  );
};

export default EditMadeIn;